import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/Layout";
import { DataTable, Pagination } from "@/components/common/DataTable";
import { StatusBadge, ScoreBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COUNTRIES, LANGUAGES, dt, title } from "@/lib/format";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { useAuth } from "@/context/AuthContext";

export default function Contacts() {
  const { can, user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ q: "", country: "", language: "", do_not_call: "" });
  const [selected, setSelected] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [assignCampaign, setAssignCampaign] = useState("");
  const [csv, setCsv] = useState({ file: null, preview: null, mapping: {}, report: null });
  const [draft, setDraft] = useState({
    first_name: "", last_name: "", phone: "", country: "AL", language: "sq",
    timezone: "Europe/Tirane", source: "manual", product_interest: "", consent_status: "given",
  });

  const params = {
    page, page_size: 25,
    q: filters.q || undefined,
    country: filters.country || undefined,
    language: filters.language || undefined,
    do_not_call: filters.do_not_call === "" ? undefined : filters.do_not_call === "true",
  };
  const { data, isLoading } = useQuery({ queryKey: ["contacts", params], queryFn: () => get("/contacts", params) });
  const { data: campaigns } = useQuery({ queryKey: ["campaign-options"], queryFn: () => get("/campaigns", { page_size: 100 }) });

  const create = useMutation({
    mutationFn: () => api.post("/contacts", draft),
    onSuccess: () => {
      toast.success("Contact created");
      setCreateOpen(false);
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const previewCsv = async (file) => {
    const body = new FormData();
    body.append("file", file);
    try {
      const { data: result } = await api.post("/contacts/import/preview", body);
      setCsv({ file, preview: result, mapping: result.suggested_mapping || {}, report: null });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const runImport = useMutation({
    mutationFn: async () => {
      const body = new FormData();
      body.append("file", csv.file);
      const { data: result } = await api.post("/contacts/import", body, {
        params: { mapping: JSON.stringify(csv.mapping), default_country: "AL", default_language: "sq", source: "csv_import" },
      });
      return result;
    },
    onSuccess: (result) => {
      setCsv((c) => ({ ...c, report: result.stats }));
      toast.success(`${result.stats.valid} contacts imported`);
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const assign = useMutation({
    mutationFn: () => api.post("/contacts/assign-campaign", { contact_ids: selected, campaign_id: assignCampaign }),
    onSuccess: ({ data: result }) => {
      const skipped = Object.entries(result.skipped || {})
        .filter(([, v]) => v.length)
        .map(([k, v]) => `${title(k)}: ${v.length}`)
        .join(", ");
      toast.success(`${result.added} contacts assigned${skipped ? ` · skipped → ${skipped}` : ""}`);
      setAssignOpen(false);
      setSelected([]);
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const removeContacts = useMutation({
    mutationFn: (ids) => (ids.length === 1
      ? api.delete(`/contacts/${ids[0]}`)
      : api.post("/contacts/bulk-delete", { contact_ids: ids })),
    onSuccess: ({ data: result }) => {
      const removed = result?.deleted ?? 0;
      toast.success(`${removed} contact${removed === 1 ? "" : "s"} deleted`);
      if (result?.busy?.length) {
        toast.error(`${result.busy.length} stayed because a call is still in progress`);
      }
      const busy = new Set(result?.busy || []);
      setSelected((current) => current.filter((id) => busy.has(id)));
      setPendingDelete(null);
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const bulkLanguage = useMutation({
    mutationFn: (language) => api.post("/contacts/bulk-edit", { contact_ids: selected, updates: { language } }),
    onSuccess: ({ data: result }) => {
      toast.success(`${result.modified} contacts updated`);
      setSelected([]);
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const exportCsv = async () => {
    try {
      const { data: result } = await api.get("/contacts/export");
      const blob = new Blob([result.content], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`${result.count} contacts exported`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const rows = data?.items || [];
  const allSelected = rows.length > 0 && rows.every((r) => selected.includes(r.id));

  return (
    <>
      <PageHeader
        testId="contacts-page"
        title="Contacts"
        subtitle="Normalized phone numbers, consent state and campaign eligibility"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={exportCsv} data-testid="contacts-export">Export CSV</Button>
            {can("contacts", "write") ? (
              <>
                <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="contacts-import-open">
                  <Upload className="mr-1 h-4 w-4" /> Import CSV
                </Button>
                <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setCreateOpen(true)} data-testid="contacts-new">
                  New contact
                </Button>
              </>
            ) : null}
          </div>
        }
      />

      <div className="panel grid grid-cols-1 gap-3 p-3 md:grid-cols-4">
        <Input
          data-testid="contacts-search"
          value={filters.q}
          onChange={(e) => {
            setFilters((f) => ({ ...f, q: e.target.value }));
            setPage(1);
          }}
          placeholder="Search name or phone"
          className="h-9"
        />
        <Select value={filters.country || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, country: v === "all" ? "" : v }))}>
          <SelectTrigger data-testid="contacts-country-filter" className="h-9"><SelectValue placeholder="Country" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All countries</SelectItem>
            {COUNTRIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.language || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, language: v === "all" ? "" : v }))}>
          <SelectTrigger data-testid="contacts-language-filter" className="h-9"><SelectValue placeholder="Language" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All languages</SelectItem>
            {LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.do_not_call || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, do_not_call: v === "all" ? "" : v }))}>
          <SelectTrigger data-testid="contacts-dnc-filter" className="h-9"><SelectValue placeholder="DNC status" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All contacts</SelectItem>
            <SelectItem value="true">On do-not-call list</SelectItem>
            <SelectItem value="false">Callable</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {selected.length && can("contacts", "write") ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2" data-testid="contacts-bulk-bar">
          <span className="text-sm font-medium text-blue-900">{selected.length} selected</span>
          <Button size="sm" variant="outline" onClick={() => setAssignOpen(true)} data-testid="contacts-bulk-assign">
            Assign to campaign
          </Button>
          <Select onValueChange={(v) => bulkLanguage.mutate(v)}>
            <SelectTrigger data-testid="contacts-bulk-language" className="h-8 w-44 bg-white"><SelectValue placeholder="Set language" /></SelectTrigger>
            <SelectContent className="bg-white">
              {LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {isAdmin ? (
            <Button
              size="sm"
              variant="outline"
              className="border-rose-200 text-rose-700 hover:bg-rose-50"
              onClick={() => setPendingDelete(selected)}
              data-testid="contacts-bulk-delete"
            >
              <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" onClick={() => setSelected([])}>Clear</Button>
        </div>
      ) : null}

      <Section testId="contacts-section" title={`${data?.total ?? 0} contacts`}>
        <DataTable
          testId="contacts-table"
          rowTestId="contact-row"
          loading={isLoading}
          columns={[
            {
              key: "select",
              label: (
                <Checkbox
                  data-testid="contacts-select-all"
                  checked={allSelected}
                  onCheckedChange={(checked) =>
                    setSelected(checked ? Array.from(new Set([...selected, ...rows.map((r) => r.id)])) : selected.filter((id) => !rows.some((r) => r.id === id)))
                  }
                />
              ),
              width: "36px",
              render: (r) => (
                <Checkbox
                  data-testid={`contact-select-${r.id}`}
                  checked={selected.includes(r.id)}
                  onCheckedChange={(checked) =>
                    setSelected((s) => (checked ? [...s, r.id] : s.filter((id) => id !== r.id)))
                  }
                />
              ),
            },
            { key: "name", label: "Name", render: (r) => <span className="font-medium text-slate-800">{`${r.first_name} ${r.last_name}`.trim()}</span> },
            { key: "phone", label: "Phone (E.164)", render: (r) => <span className="font-mono text-xs">{r.phone}</span> },
            { key: "country", label: "Country" },
            { key: "language", label: "Lang", render: (r) => (r.language || "").toUpperCase() },
            { key: "timezone", label: "Timezone" },
            { key: "source", label: "Source", render: (r) => title(r.source) },
            { key: "product_interest", label: "Interest", render: (r) => r.product_interest || "—" },
            { key: "consent_status", label: "Consent", render: (r) => <StatusBadge value={r.consent_status} /> },
            {
              key: "do_not_call",
              label: "DNC",
              render: (r) => (r.do_not_call ? <StatusBadge value="do_not_call" label="Blocked" /> : <span className="text-slate-400">—</span>),
            },
            { key: "attempts", label: "Attempts" },
            { key: "last_call_at", label: "Last call", render: (r) => dt(r.last_call_at) },
            { key: "last_result", label: "Last result", render: (r) => (r.last_result ? <StatusBadge value={r.last_result} /> : "—") },
            { key: "interest_score", label: "Score", render: (r) => <ScoreBadge score={r.interest_score} /> },
            ...(isAdmin ? [{
              key: "actions",
              label: "",
              render: (r) => (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                  data-testid={`contact-delete-${r.id}`}
                  onClick={() => setPendingDelete([r.id])}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              ),
            }] : []),
          ]}
          rows={rows}
          empty={{ title: "No contacts found", description: "Import a CSV file or create a contact manually." }}
        />
        <Pagination page={data?.page || 1} pages={data?.pages || 1} total={data?.total || 0} onChange={setPage} />
      </Section>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>New contact</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>First name</Label>
              <Input data-testid="contact-first-name" value={draft.first_name} onChange={(e) => setDraft({ ...draft, first_name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Last name</Label>
              <Input data-testid="contact-last-name" value={draft.last_name} onChange={(e) => setDraft({ ...draft, last_name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input data-testid="contact-phone" value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} placeholder="+355 69 123 4567" />
            </div>
            <div className="space-y-1.5">
              <Label>Product interest</Label>
              <Input data-testid="contact-product" value={draft.product_interest} onChange={(e) => setDraft({ ...draft, product_interest: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Country</Label>
              <Select value={draft.country} onValueChange={(v) => setDraft({ ...draft, country: v })}>
                <SelectTrigger data-testid="contact-country"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {COUNTRIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Language</Label>
              <Select value={draft.language} onValueChange={(v) => setDraft({ ...draft, language: v })}>
                <SelectTrigger data-testid="contact-language"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {LANGUAGES.map((l) => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Consent status</Label>
              <Select value={draft.consent_status} onValueChange={(v) => setDraft({ ...draft, consent_status: v })}>
                <SelectTrigger data-testid="contact-consent"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="given">Given</SelectItem>
                  <SelectItem value="unknown">Unknown</SelectItem>
                  <SelectItem value="refused">Refused</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              disabled={!draft.first_name || !draft.phone || create.isPending}
              onClick={() => create.mutate()}
              data-testid="contact-create-submit"
            >
              Create contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader><DialogTitle>Import contacts from CSV</DialogTitle></DialogHeader>
          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
            <Upload className="h-4 w-4" />
            {csv.file ? csv.file.name : "Choose a .csv file"}
            <input
              data-testid="contacts-csv-input"
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && previewCsv(e.target.files[0])}
            />
          </label>
          {csv.preview ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">{csv.preview.row_count} rows detected. Map the columns:</p>
              <div className="grid grid-cols-2 gap-3">
                {["first_name", "last_name", "phone", "country", "language", "product_interest"].map((field) => (
                  <div key={field} className="space-y-1.5">
                    <Label>{title(field)}{field === "phone" ? " *" : ""}</Label>
                    <Select
                      value={csv.mapping[field] || "none"}
                      onValueChange={(v) => setCsv((c) => ({ ...c, mapping: { ...c.mapping, [field]: v === "none" ? undefined : v } }))}
                    >
                      <SelectTrigger data-testid={`contacts-map-${field}`}><SelectValue placeholder="Not mapped" /></SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="none">Not mapped</SelectItem>
                        {csv.preview.headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          {csv.report ? (
            <div className="grid grid-cols-5 gap-2" data-testid="contacts-import-report">
              {[["Total", csv.report.total], ["Valid", csv.report.valid], ["Invalid", csv.report.invalid], ["Duplicates", csv.report.duplicates], ["Blocked", csv.report.blocked]].map(([k, v]) => (
                <div key={k} className="rounded-md border border-slate-200 px-2 py-1.5">
                  <p className="kpi-label">{k}</p>
                  <p className="text-lg font-semibold text-slate-900">{v}</p>
                </div>
              ))}
            </div>
          ) : null}
          <DialogFooter>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              disabled={!csv.mapping.phone || runImport.isPending}
              onClick={() => runImport.mutate()}
              data-testid="contacts-import-submit"
            >
              {runImport.isPending ? "Importing…" : "Import contacts"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Assign {selected.length} contacts to a campaign</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            Each contact is validated for phone format, do-not-call status, duplicate membership,
            attempt limit, allowed country and consent before being added.
          </p>
          <Select value={assignCampaign} onValueChange={setAssignCampaign}>
            <SelectTrigger data-testid="assign-campaign-select"><SelectValue placeholder="Select campaign" /></SelectTrigger>
            <SelectContent className="bg-white">
              {(campaigns?.items || []).filter((c) => c.status !== "completed").map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name} · {title(c.status)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              disabled={!assignCampaign || assign.isPending}
              onClick={() => assign.mutate()}
              data-testid="assign-campaign-submit"
            >
              Validate and assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={`Delete ${pendingDelete?.length === 1 ? "this contact" : `${pendingDelete?.length || 0} contacts`}?`}
        description="They leave the contact list and any campaign queue. Call history stays. A contact on a live call is kept until that call ends."
        confirmLabel="Delete"
        destructive
        onConfirm={() => removeContacts.mutateAsync(pendingDelete)}
        testId="contacts-delete-confirm"
      />

    </>
  );
}